import * as $ from 'jquery';
import '/modules/common/css/ui/slider.css';
import '/modules/common/css/ui/slider.mobile.css';
import {UIElement} from "./ui";

/**
 * A slider to select numeric values
 * @extends UIElement
 */
class Slider extends UIElement {

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param id {string} Slider id
     * @param min {number} The minimum value for the slider
     * @param max {number} The maximum value for the slider
     * @param value {number} The initial value
     * @param step {number} Step size. The precision of this slider
     * @param tickCount {number} How many ticks to show (integer). If undefined, no ticks are shown
     * @param labelFormatter {function} A function that takes a value and format's it to show the user. Can be undefined
     * @param legendFormatter {function} A function that takes a value and returns a legend to add to the slider
     * @param onChange {function} A callback to be called when the value changes
     * @param delayChange {boolean} Should the call to the onChange callback be slightly delayed?
     * @param isInteger {boolean} Should this slider only return integer values?
     * @param parentSlider {Slider} A parent slider. When this slider is in the min/max value and the user clicks next/previous, checks with the parent slider if there are next/previous values
     * @param getMaxForParentValue {function} If there is a parentSlider, this function is going to be called to get the next value for this slider when it is in the min value and the user clicks previous
     * @param getMinForParentValue {function} If there is a parentSlider, this function is going to be called to get the previous value for this slider when it is in the max value and the user clicks next
     */
    constructor(translator, isMobileDevice, id, min, max, value, step, tickCount, labelFormatter, legendFormatter, onChange, delayChange, isInteger, parentSlider = undefined, getMaxForParentValue = undefined, getMinForParentValue = undefined) {
        super(id, translator, isMobileDevice, undefined, undefined);
        this.min = min;
        this.max = max;
        this.value = value !== undefined ? value : min;
        if (min !== undefined) {
            this.value = Math.max(this.min, this.value);
        }
        if (max !== undefined && this.value !== undefined) {
            this.value = Math.min(this.max, this.value);
        }
        this.step = step;
        this.tickCount = tickCount === 0 ? 0 : Math.max(1, tickCount);
        this.labelFormatter = labelFormatter;
        this.legendFormatter = legendFormatter;
        this.isInteger = isInteger;
        this.parentSlider = parentSlider;
        this.onChangeCallbacks = new Set();
        this.onChange(onChange);
        this.delayChange = delayChange;
        this.changeImmediately = !this.delayChange;

        this.getMinForParentValue = getMinForParentValue;
        this.getMaxForParentValue = getMaxForParentValue;

        if (this.parentSlider) {
            this.parentSlider.onChange(parent => {
                this._checkEnabledNextPrevious();
            });
        }

        if (this.value < this.min) {
            this.setValue(this.min);
        }
        if (this.value > this.max) {
            this.setValue(this.max);
        }
    }

    getClassName() {
        return "Slider";
    }

    handleLabel(count, index, lastIndex, option, tick, formattedValue) {
        const midIndex = Math.floor((count + 1) / 2 - 1);
        const hasSecondMiddleTick = index => count > 6 && count <= 13 && count % 2 === 0 && index === midIndex + 1;
        if (index === 0) {
            this.addLabel(option, tick, formattedValue);
            return true;
        }
        if (this.tickCount < 4) {
            this.addLabel(option, tick, formattedValue);
            return true;
        }
        if (this.tickCount > 4 && index === midIndex) {
            this.addLabel(option, tick, formattedValue, hasSecondMiddleTick(index + 1) && formattedValue.length > 2);
            return true;
        }
        if (hasSecondMiddleTick(index)) {
            this.addLabel(option, tick, formattedValue, formattedValue.length > 2);
            return true;
        }
        if (index === lastIndex) {
            this.addLabel(option, tick, formattedValue);
            return true;
        }
        return false;
    }

    addLabel(option, element, formattedValue, rotate=false) {
        option.attr('label', formattedValue);
        $('div.label', element).html(formattedValue).attr('title', formattedValue);
        if (rotate) {
            $('div.label', element).addClass('rotated');
        }
    }

    getUIElement() {
        if (!this.uiElement) {
            const labelFormatter = this._getLabelFormatter();
            this.uiElement = $(
                '<div id="' + this.id + '" class="slider-input">' +
                '   <div class="slider-header">' +
                '       <div class="previous-value"><button title="' + this.translator.translate('slider.previous.button.name') + '" class="slider-previous-button"></button></div>' +
                '       <div class="value">' + this.formatSelectedValue(this.value) + '</div>' +
                '       <div class="next-value"><button title="' + this.translator.translate('slider.next.button.name') + '" class="slider-next-button"></button></div>' +
                '   </div>' +
                '   <input type="range" min="' + this.min + '" max="' + this.max + '" value="' + this.value + '" step="' + this.step + '" />' +
                '</div>'
            );

            if (this.min === this.max) {
                this.uiElement.addClass('disabled');
                $('input[type=range]', this.uiElement).attr('disabled', 'disabled');
            }

            if (this.tickCount) {
                const tickMarksId = this.id + '_tickmarks';
                $('input[type=range]', this.uiElement).attr('list', tickMarksId);
                const dataList = $('<datalist id="' + tickMarksId + '"></datalist>');
                const tickMarks = $('<div class="slider-ticks"></div>');
                let increment = Number(((this.max - this.min) / (this.tickCount - 1)).toFixed(10));
                let count = this.tickCount;
                let lastIndex = count - 1;

                if (this.isInteger && this.min < this.max) {
                    increment = Math.floor(increment);
                    while ((this.max - this.min) % increment !== 0) {
                        increment++;
                        if (increment > (this.max - this.min) / 2) {
                            increment = 1;
                            break;
                        }
                    }
                    count = ((this.max - this.min) + 1) / increment;
                    lastIndex = count - 1;
                } else increment = Number(increment.toFixed(10)); //avoid floating point error
                let index = 0;
                this.markedValues = [];

                const self = this;

                if (this.min <= this.max) {
                    for (let value = this.min; value <= this.max; value = Number((value + increment).toFixed(10))) {
                        const formattedValue = labelFormatter(Number(value));
                        this.markedValues.push(value);
                        const option = $('<option value="' + formattedValue + '"></option>');
                        dataList.append(option);
                        const tick = $('<div><div class="label"></div></div>');
                        tickMarks.append(tick);
                        if (!this.handleLabel(count, index, lastIndex, option, tick, formattedValue))
                            tick.attr('title', formattedValue);
                        tick.data('value', value);
                        tick.click(function () {
                            const rangeInput = $('input[type=range]', self.uiElement);
                            rangeInput.val($(this).data('value'));
                            self._onChangeSlider(false);
                            self.logStat("TICK {" + $(this).data('value') + "}");
                        });
                        index++;
                    }
                }
                if (index > 50) {
                    tickMarks.addClass('many-marks')
                }
                if (index > 65)
                    tickMarks.addClass('too-many-marks')
                this.uiElement.append(tickMarks);
                this.uiElement.append(dataList);

                $('.previous-value button', this.uiElement).click(() => {
                    this.previous();
                    this.logStat("PREVIOUS {" + this.value + "}");
                });
                $('.next-value button', this.uiElement).click(() => {
                    this.next();
                    this.logStat("NEXT {" + this.value + "}");
                });
                this._checkEnabledNextPrevious();
            } else {
                $('.previous-value', this.uiElement).hide();
                $('.next-value', this.uiElement).hide();
            }

            this.uiElement.append($('<div class="slider-legend"></div>'));
            this._updateLegend();

            $('input[type=range]', this.uiElement).change(() => {
                this._onChangeSlider(true);
            }).on('input', function () { //let's hack it to make sure we update the value as the user slides and not just at the end
                $(this).trigger('change');
            });
        }
        return this.uiElement;
    }

    _onChangeSlider(fromUser) {
        const value = Number($('input[type=range]', this.uiElement).val());
        if (fromUser && !this.changeImmediately) {
            if (this.changeTimeout) {
                clearTimeout(this.changeTimeout);
            }
            this.changeTimeout = setTimeout(() => {
                this._executeOnChange(value);
            }, 500);
        } else {
            this._executeOnChange(value);
        }
        if (fromUser) {
            this.logStat("CHANGE {" + value + "}");
        }
    }

    _getLabelFormatter() {
        return this.labelFormatter ? this.labelFormatter : function (value) {
            return Number(value.toFixed(0)) + '';
        };
    }

    formatSelectedValue(value){
        return this._getLabelFormatter()(value);
    }

    _executeOnChange(newValue) {
        this.value = newValue;
        $('div.value', this.getUIElement()).empty().html(this.formatSelectedValue(this.value));
        this.onChangeCallbacks.forEach(c=>c(this))
        this._checkEnabledNextPrevious();
        this._updateLegend();
    }

    /**
     * Go to the next tick value
     */
    next(fromUser = true) {
        this.changeImmediately = true;
        const rangeInput = $('input[type=range]', this.uiElement);
        if (this.value < this.max) {
            rangeInput.val(this.markedValues[this._getGreaterMarkedValueIndexFor(this.value)]);
            this._onChangeSlider(fromUser);
        } else if (this.parentSlider && this.parentSlider.hasNext()) {
            const val = this.getMinForParentValue.call(this, this.parentSlider._getNextValue());
            rangeInput.attr('min', val);
            rangeInput.val(val);
            this._onChangeSlider(false);
            this.parentSlider.next();
        }
        this.changeImmediately = !this.delayChange;
    }

    _getNextValue() {
        if (this.value < this.max) {
            return this.markedValues[this._getGreaterMarkedValueIndexFor(this.value)];
        } else if (this.parentSlider && this.parentSlider.hasNext()) {
            return this.getMinForParentValue.call(this, this.parentSlider._getNextValue());
        }
    }

    /**
     * Go to the previous tick value
     */
    previous(fromUser = true) {
        this.changeImmediately = true;
        const rangeInput = $('input[type=range]', this.uiElement);
        if (this.value > this.min) {
            rangeInput.val(this.markedValues[this._getLesserMarkedValueIndexFor(this.value)]);
            this._onChangeSlider(fromUser);
        } else if (this.parentSlider && this.parentSlider.hasPrevious()) {
            this.value = this.getMaxForParentValue.call(this, this.parentSlider._getPreviousValue());
            this.max = this.value;
            rangeInput.attr('max', this.value).attr('value', this.value).val(this.value);
            this.parentSlider.previous();
            this._onChangeSlider(false);
        }
        this.changeImmediately = !this.delayChange;
    }

    _getPreviousValue() {
        if (this.value > this.min) {
            return this.markedValues[this._getLesserMarkedValueIndexFor(this.value)];
        } else if (this.parentSlider && this.parentSlider.hasPrevious()) {
            return this.getMaxForParentValue.call(this, this.parentSlider._getPreviousValue());
        }
    }

    _checkEnabledNextPrevious() {
        if (!this.hasNext()) {
            $('.next-value button', this.uiElement).attr('disabled', 'disabled').addClass('disabled');
        } else {
            $('.next-value button', this.uiElement).removeAttr('disabled').removeClass('disabled');
        }
        if (!this.hasPrevious()) {
            $('.previous-value button', this.uiElement).attr('disabled', 'disabled').addClass('disabled');
        } else {
            $('.previous-value button', this.uiElement).removeAttr('disabled').removeClass('disabled');
        }
    }

    _updateLegend() {
        if (this.legendFormatter !== undefined) {
            $('.slider-legend', this.getUIElement()).empty().append(this.legendFormatter(this.getValue()));
        }
    }

    /**
     * Add callback to execute on change
     * @param callback {function} A callback that takes this slider
     */
    onChange(callback) {
        if (callback) {
            this.onChangeCallbacks.add(callback);
        }
        return this;
    }

    _getGreaterMarkedValueIndexFor(value) {
        for (let i = 0; i < this.markedValues.length; i++) {
            if (this.markedValues[i] > value)
                return i;
        }
        return 0;
    }

    _getLesserMarkedValueIndexFor(value) {
        for (let i = this.markedValues.length - 1; i >= 0; i--) {
            if (this.markedValues[i] < value)
                return i;
        }
        return this.markedValues.length - 1;
    }

    /**
     * Does this slider have a previous value?
     * @return {boolean}
     */
    hasPrevious() {
        if (this.value > this.min) {
            return true;
        }
        if (this.parentSlider) {
            return this.parentSlider.hasPrevious();
        }
    }

    /**
     * Does this slider have a next value?
     * @return {boolean}
     */
    hasNext() {
        if (this.value < this.max) {
            return true;
        }
        if (this.parentSlider) {
            return this.parentSlider.hasNext();
        }
    }

    getCode() {
        return this.id;
    }

    /**
     * This slider's current value
     * @return {number}
     */
    getValue() {
        return this.value;
    }

    /**
     * Set this slider's value
     * @param value {number} The value
     */
    setValue(value) {
        const rangeInput = $('input[type=range]', this.uiElement);
        rangeInput.val(value);
        this._onChangeSlider(false);
    }
}

export {Slider}